import i18n from "@/i18n";
import type { Reflection } from "@/types";

const WHERE_KEYS: Record<NonNullable<Reflection["where"]>, string> = {
  opncue: "growth.options.where.opncue",
  somewhere_else: "growth.options.where.somewhereElse",
};

const OUTCOME_KEYS: Partial<
  Record<NonNullable<Reflection["outcome"]>, string>
> = {
  made_connection: "growth.options.outcome.madeConnection",
  clicked: "growth.options.outcome.clicked",
  practiced: "growth.options.outcome.practiced",
  learned_something: "growth.options.outcome.learnedSomething",
  more_comfortable_than_expected: "growth.options.outcome.moreComfortable",
  nervous_but_stayed: "growth.options.outcome.nervousStayed",
  learned_about_them: "growth.options.outcome.learnedAboutThem",
  learned_about_myself: "growth.options.outcome.learnedAboutMyself",
  one_sided: "growth.options.outcome.oneSided",
  not_my_vibe: "growth.options.outcome.notMyVibe",
  rested: "growth.options.outcome.rested",
  no_outcome_yet: "growth.options.outcome.noOutcomeYet",
  nothing_in_particular: "growth.options.outcome.nothingInParticular",
  custom: "growth.options.outcome.other",
};

const FEELING_KEYS: Partial<
  Record<NonNullable<Reflection["feeling"]>, string>
> = {
  good: "growth.options.feeling.good",
  okay: "growth.options.feeling.okay",
  energised: "growth.options.feeling.energised",
  awkward: "growth.options.feeling.awkward",
  hard_but_tried: "growth.options.feeling.hardButTried",
  proud: "growth.options.feeling.proud",
  comfortable: "growth.options.feeling.comfortable",
  curious: "growth.options.feeling.curious",
  unsure: "growth.options.feeling.unsure",
  drained: "growth.options.feeling.drained",
  disappointed: "growth.options.feeling.disappointed",
  not_my_vibe: "growth.options.feeling.notMyVibe",
  quiet: "growth.options.feeling.quiet",
  custom: "growth.options.feeling.other",
};

const SUBJECT_KEYS: Partial<
  Record<NonNullable<Reflection["subject"]>, string>
> = {
  a_cue: "growth.options.subject.aCue",
  a_conversation: "growth.options.subject.aConversation",
  a_group: "growth.options.subject.aGroup",
  said_hi: "growth.options.subject.saidHi",
  started_conversation: "growth.options.subject.startedConversation",
  joined_group: "growth.options.subject.joinedGroup",
  joined_in: "growth.options.subject.joinedIn",
  went_somewhere_social: "growth.options.subject.wentSomewhereSocial",
  replied_to_someone: "growth.options.subject.repliedToSomeone",
  reached_out: "growth.options.subject.reachedOut",
  met_someone_new: "growth.options.subject.metSomeoneNew",
  made_plan: "growth.options.subject.madePlan",
  tried_something_difficult: "growth.options.subject.triedSomethingDifficult",
  stepped_away: "growth.options.subject.steppedAway",
  showed_up: "growth.options.subject.showedUp",
  rested: "growth.options.subject.rested",
  custom: "growth.options.subject.other",
};

function translateKey(key: string | undefined): string | undefined {
  return key ? i18n.t(key) : undefined;
}

export function reflectionWhereLabel(
  value: NonNullable<Reflection["where"]>,
): string {
  return i18n.t(WHERE_KEYS[value]);
}

export function reflectionOutcomeLabel(
  value: NonNullable<Reflection["outcome"]>,
): string | undefined {
  return translateKey(OUTCOME_KEYS[value]);
}

export function reflectionFeelingLabel(
  value: NonNullable<Reflection["feeling"]>,
): string | undefined {
  return translateKey(FEELING_KEYS[value]);
}

/**
 * The catalog KEY for a feeling, not its translation.
 *
 * For callers that build a table at module scope. `reflectionFeelingLabel`
 * resolves immediately, so a list defined at import time freezes whatever
 * language i18n happened to hold — which on a cold start is English, and after
 * a language change is the previous one. Take the key and resolve it at render.
 */
export function reflectionFeelingKey(
  value: NonNullable<Reflection["feeling"]>,
): string | undefined {
  return FEELING_KEYS[value];
}

export function reflectionSubjectLabel(
  value: NonNullable<Reflection["subject"]>,
): string | undefined {
  return translateKey(SUBJECT_KEYS[value]);
}

/** A short one-line title for a reflection ("opncue. I made a connection"). */
export function reflectionTitle(r: Reflection): string {
  const source = r.where
    ? reflectionWhereLabel(r.where)
    : i18n.t("growth.reflection").toLocaleLowerCase(i18n.resolvedLanguage);
  const outcome = r.outcome ? reflectionOutcomeLabel(r.outcome) : undefined;
  return outcome ? `${source}. ${outcome}` : source;
}

/** Human date + time for a reflection's `createdAt`. */
export function reflectionDateTime(iso: string): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}
